import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { ObservableResult } from '@app/app/shared/store';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';
import {
  CohortGradeData,
  CohortOption,
  ProgramCourseItem,
  ProgramCoursesAnalyticsFilters,
  ProgramCoursesOverview,
  StudentEngagementData,
} from '../../models/courses-analytics.model';
import { CoursesAnalyticsDataService } from './courses-analytics-data.service';

@Injectable()
export class ApiCoursesAnalyticsDataService implements CoursesAnalyticsDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getOverview(programId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<ProgramCoursesOverview> {
    const params = this.buildPlaylistParams(filters);
    return this.client
      .get<ProgramCoursesOverview>(
        Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/courses/overview`),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsOverview'))),
      );
  }

  getCourses(programId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<ProgramCourseItem[]> {
    const params: Record<string, string> = {};
    if (filters?.searchQuery) {
      params['search'] = filters.searchQuery;
    }
    if (filters?.statusFilter && filters.statusFilter !== 'all') {
      params['status'] = filters.statusFilter === 'active' ? 'Active' : 'Ended';
    }
    if (filters?.cohort) {
      params['cohort'] = filters.cohort;
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }

    return this.client
      .get<ProgramCourseItem[]>(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/courses`), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCourses'))),
      );
  }

  getCohortGradeComparison(programId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<CohortGradeData[]> {
    const params: Record<string, string> = {};
    if (filters?.cohortNames?.length) {
      params['cohortNames'] = filters.cohortNames.join(',');
    }
    if (filters?.metric) {
      params['metric'] = filters.metric;
    }
    if (filters?.dateRange?.start) {
      params['startDate'] = filters.dateRange.start.toISOString();
    }
    if (filters?.dateRange?.end) {
      params['endDate'] = filters.dateRange.end.toISOString();
    }

    return this.client
      .get<
        CohortGradeData[]
      >(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/courses/cohort-comparison`), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() =>
          ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCohortComparison')),
        ),
      );
  }

  getStudentEngagement(programId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<StudentEngagementData> {
    const params = this.buildPlaylistParams(filters);

    return this.client
      .get<StudentEngagementData>(
        Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/courses/engagement`),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsEngagement'))),
      );
  }

  getCohorts(programId: string): ObservableResult<CohortOption[]> {
    return this.client
      .get<CohortOption[]>(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/cohorts`))
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCohorts'))),
      );
  }
  private buildPlaylistParams(filters?: ProgramCoursesAnalyticsFilters): Record<string, string> {
    const params: Record<string, string> = {};
    if (filters?.includedPlaylistIds?.length) {
      params['playlistIds'] = filters.includedPlaylistIds.join(',');
    }
    if (filters?.cohort) {
      params['cohort'] = filters.cohort;
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }
    return params;
  }
}
